import { useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { api } from '../config/ApiConfig';
import { useMeQuery } from '../modules/Auth/api/useMeQuery';
import { Loader } from '../routes/AppRoutes';
import { getDefaultPathForRole } from '../routes/getDefaultPathForRole';
import { Paths } from '../routes/Paths';
import { CoenergyUserRole } from '../types/CoenergyUserRole';
import {
  clearAuthTokens,
  hasAuthSession,
  persistAuthSession,
} from '../utils/authTokenStorage';

export type AuthUser = {
  _id: string;
  name: string;
  email: string;
  role: CoenergyUserRole;
  partnerId?: string;
};

export type AuthData = {
  user: AuthUser;
  accessToken?: string;
  refreshToken?: string;
};

type AuthUserProviderProps = {
  getCurrentAccessToken: () => string | undefined;
  logout: () => void;
  login: (data: AuthData, rememberMe?: boolean) => void;
  user?: AuthUser;
  isLoading: boolean;
};

export const AuthUserProviderContext = createContext<AuthUserProviderProps>({
  getCurrentAccessToken: () => undefined,
  logout: () => {},
  user: {} as AuthUser,
  isLoading: true,
  login: () => {},
});

export const AuthUserProvider = ({ children }: React.PropsWithChildren) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data, isSuccess, error, isLoading } = useMeQuery();
  const [user, setUser] = useState<AuthUser | undefined>(undefined);
  const shouldClearCacheAfterLogoutRef = useRef(false);
  const navigate = useNavigate();

  const getCurrentAccessToken = () => undefined;

  const isPublicRoute =
    location.pathname.includes('login') ||
    location.pathname.includes('recovery-password');

  const logout = () => {
    clearAuthTokens();
    setUser(undefined);
    shouldClearCacheAfterLogoutRef.current = true;

    void api.post('/v1/auth/logout').catch(() => undefined);

    if (isPublicRoute) {
      shouldClearCacheAfterLogoutRef.current = false;
      queryClient.clear();
      return;
    }

    navigate(Paths.Login, { replace: true });
  };

  const login = (data: AuthData, rememberMe = false) => {
    persistAuthSession({ rememberMe });

    queryClient.setQueryData(['me'], data.user);
    queryClient.removeQueries({ queryKey: ['partner-portal'] });

    setUser(data.user);

    navigate(getDefaultPathForRole(data.user.role), { replace: true });
  };

  useEffect(() => {
    if (isSuccess && data && hasAuthSession()) {
      setUser(data);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (
      !shouldClearCacheAfterLogoutRef.current ||
      location.pathname !== Paths.Login
    ) {
      return;
    }

    shouldClearCacheAfterLogoutRef.current = false;
    queryClient.clear();
  }, [location.pathname, queryClient]);

  useEffect(() => {
    if (!error || isPublicRoute || !hasAuthSession()) {
      return;
    }

    logout();
  }, [error, isPublicRoute]);

  const value = useMemo(
    () => ({
      user,
      getCurrentAccessToken,
      logout,
      isLoading,
      login,
    }),
    [user, isLoading],
  );

  return (
    <AuthUserProviderContext.Provider value={value}>
      <Loader
        open={isLoading && !isPublicRoute && location.pathname === Paths.Root}
      />
      {children}
    </AuthUserProviderContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthUserProviderContext);
};
